import type { ActivityMessage } from "@/api/models"

export type WorkflowTestRunActivity = {
  nodeId: string
  message: string
  timestamp: number
}

export type WorkflowActivityTranslator = (key: string, defaultValue: string) => string

const defaultTranslator: WorkflowActivityTranslator = (_key, defaultValue) => defaultValue

function text(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function number(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

/**
 * Convert only content-free workflow diagnostics into editor timeline rows.
 * Queries, result snippets, node I/O, and arbitrary tool messages are never
 * copied from the wire payload.
 */
export function toWorkflowTestRunActivity(
  activity: ActivityMessage,
  translate: WorkflowActivityTranslator = defaultTranslator
): WorkflowTestRunActivity | null {
  const details = activity.details
  const nodeId = text(details?.node_id)
  if (!nodeId) return null

  const tool = text(details?.tool)
  const errorCode = text(details?.error_code)
  if (details?.timed_out === true) {
    return {
      nodeId,
      message: `${translate("runs.diagnostics.toolTimedOut", "Tool timed out")}${tool ? `: ${tool}` : ""}`,
      timestamp: activity.timestamp,
    }
  }
  if (details?.skipped === true) {
    const suffix = errorCode ? ` (${errorCode})` : ""
    return {
      nodeId,
      message: `${translate("runs.diagnostics.skipped", "Skipped")}${tool ? `: ${tool}` : ""}${suffix}`,
      timestamp: activity.timestamp,
    }
  }

  const effectiveModel = text(details?.effective_model)
  if (effectiveModel) {
    const reasoning = text(details?.reasoning_effort)
    return {
      nodeId,
      message: `${translate("runs.diagnostics.model", "Model")}: ${effectiveModel}${
        reasoning ? ` · ${translate("runs.diagnostics.reasoning", "Reasoning")}: ${reasoning}` : ""
      }`,
      timestamp: activity.timestamp,
    }
  }

  const fromModel = text(details?.from_model)
  const toModel = text(details?.to_model)
  if (fromModel && toModel) {
    return {
      nodeId,
      message: `${translate("runs.diagnostics.modelFallbackEvent", "Model fallback")}: ${fromModel} → ${toModel}`,
      timestamp: activity.timestamp,
    }
  }

  // web_search_results carries source previews for the citation drawer. The
  // tool loop emits one separate, content-free web_search event for diagnostics.
  // Accept only the latter so a successful search is never shown twice here.
  const isWebSearch = activity.activity_type === "web_search" && tool === "web_search"
  if (isWebSearch) {
    const resultCount = number(details?.result_count) ?? number(details?.count)
    const freshness = text(details?.freshness)
    const dateFrom = text(details?.date_from)
    const dateTo = text(details?.date_to)
    const status = text(details?.status)
    const statusLabels: Partial<Record<string, [string, string]>> = {
      started: ["runs.diagnostics.webSearchStarted", "Web search started"],
      completed: ["runs.diagnostics.webSearchCompleted", "Web search completed"],
      error: ["runs.diagnostics.webSearchFailed", "Web search failed"],
      timeout: ["runs.diagnostics.webSearchTimedOut", "Web search timed out"],
      cancelled: ["runs.diagnostics.webSearchCancelled", "Web search cancelled"],
    }
    const statusLabel = statusLabels[status ?? ""] ?? statusLabels.completed!
    const freshnessLabels: Partial<Record<string, [string, string]>> = {
      pd: ["runs.diagnostics.freshnessPastDay", "Last 24 hours"],
      pw: ["runs.diagnostics.freshnessPastWeek", "Last week"],
      pm: ["runs.diagnostics.freshnessPastMonth", "Last month"],
      py: ["runs.diagnostics.freshnessPastYear", "Last year"],
    }
    const freshnessLabel = freshness ? freshnessLabels[freshness] : undefined
    const parts = [
      translate(statusLabel[0], statusLabel[1]),
      details?.partial === true ? translate("runs.diagnostics.partial", "Partial") : undefined,
      resultCount !== undefined
        ? `${translate("runs.diagnostics.results", "Results")}: ${resultCount}`
        : undefined,
      freshness
        ? `${translate("runs.diagnostics.freshness", "Freshness")}: ${
            freshnessLabel ? translate(freshnessLabel[0], freshnessLabel[1]) : freshness
          }`
        : undefined,
      dateFrom ? `${translate("runs.diagnostics.dateFrom", "From")}: ${dateFrom}` : undefined,
      dateTo ? `${translate("runs.diagnostics.dateTo", "To")}: ${dateTo}` : undefined,
    ].filter(Boolean)
    return { nodeId, message: parts.join(" · "), timestamp: activity.timestamp }
  }

  // Tool-start callbacks are generated by GraphBuilder from an allow-listed
  // tool id and contain no model input or result content.
  if (activity.activity_type === "tools" && tool) {
    return {
      nodeId,
      message: `${translate("runs.diagnostics.toolActivity", "Tool activity")}: ${tool}`,
      timestamp: activity.timestamp,
    }
  }

  return null
}
